// Sales → Pipeline: prospects grouped by stage (lead → won / lost). A lost
// prospect needs a reason (the server refuses one without). A won prospect
// can hand its details to a new access code.

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fill } from "../../i18n/dictionary";
import { PROSPECT_STAGES, type Prospect, type ProspectFields, type ProspectStage } from "../../data/sales";
import type { OrgKind } from "../../data/types";
import { Button, Card, ErrorPanel, Field, INPUT, LoadingLine, formatDate, looksLikeEmail, todayIso, useLoad, type Tone } from "../admin/ui";
import { ReadOnlyNote, SectionTitle, StatusLine, salesErrorCopy, useSales } from "./shared";
import type { CodePrefill } from "./Codes";

export function groupByStage(list: Prospect[]): Record<ProspectStage, Prospect[]> {
  const out = Object.fromEntries(PROSPECT_STAGES.map((s) => [s, [] as Prospect[]])) as Record<ProspectStage, Prospect[]>;
  for (const p of list) out[p.stage].push(p);
  return out;
}

export function Pipeline() {
  const { ds, t, can } = useSales();
  const load = useLoad(() => ds.prospects(), [ds]);
  const [editing, setEditing] = useState<Prospect | "new" | null>(null);
  const navigate = useNavigate();

  function createCode(p: Prospect) {
    const prefill: CodePrefill = { from: p.name, label: p.name, kind: p.kind, families: p.expectedLicences };
    navigate("/sales/codes", { state: { prefill } });
  }

  return (
    <section aria-labelledby="pipeline-title">
      <SectionTitle
        id="pipeline-title"
        action={
          can("pipeline") ? (
            <Button onClick={() => setEditing("new")} aria-expanded={editing === "new"}>
              {t.pipeline.add}
            </Button>
          ) : undefined
        }
      >
        {t.pipeline.title}
      </SectionTitle>
      {!can("pipeline") && <ReadOnlyNote />}

      {editing && (
        <div style={{ margin: "12px 0 18px" }}>
          <ProspectForm
            key={editing === "new" ? "new" : editing.id}
            prospect={editing === "new" ? null : editing}
            onDone={(saved) => {
              setEditing(null);
              if (saved) void load.refresh();
            }}
          />
        </div>
      )}

      {load.loading && !load.data ? (
        <LoadingLine label={t.common.loading} />
      ) : load.error || !load.data ? (
        <ErrorPanel title={t.common.errorTitle} message={salesErrorCopy(t, load.error)} onRetry={load.reload} retryLabel={t.common.retry} />
      ) : (
        <Board list={load.data} onEdit={can("pipeline") ? setEditing : undefined} onCreateCode={can("codes") ? createCode : undefined} />
      )}
    </section>
  );
}

function Board({ list, onEdit, onCreateCode }: { list: Prospect[]; onEdit?: (p: Prospect) => void; onCreateCode?: (p: Prospect) => void }) {
  const { t, locale } = useSales();
  const groups = groupByStage(list);
  const today = todayIso();
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 230px), 1fr))", gap: 12, marginTop: 12 }}>
      {PROSPECT_STAGES.map((stage) => (
        <section
          key={stage}
          aria-labelledby={`stage-${stage}`}
          style={{ background: "var(--surface-warm)", borderRadius: "var(--r-lg)", padding: 12, border: "1px solid var(--line)", minWidth: 0 }}
        >
          <h3 id={`stage-${stage}`} style={{ fontSize: 15, color: "var(--ink-deep)", margin: "0 0 10px", display: "flex", justifyContent: "space-between", gap: 8 }}>
            <span>{t.pipeline[`stage_${stage}`]}</span>
            <span style={{ color: "var(--ink-faint)", fontWeight: 600 }}>{groups[stage].length}</span>
          </h3>
          {groups[stage].length === 0 ? (
            <p style={{ margin: 0, fontSize: 13.5, color: "var(--ink-faint)" }}>{t.pipeline.empty}</p>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
              {groups[stage].map((p) => {
                const late = p.nextActionOn != null && p.nextActionOn < today && stage !== "won" && stage !== "lost";
                return (
                  <li key={p.id} style={{ background: "var(--surface)", borderRadius: "var(--r-md)", padding: 10, border: "1px solid var(--line)", overflowWrap: "anywhere" }}>
                    <p style={{ margin: 0, fontWeight: 700, color: "var(--ink-deep)", fontSize: 14.5 }}>{p.name}</p>
                    <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "var(--ink-faint)" }}>
                      {p.kind === "individual" ? t.common.kindIndividual : t.common.kindOrg}
                      {p.contactName ? ` · ${p.contactName}` : ""}
                    </p>
                    {p.expectedLicences != null && (
                      <p style={{ margin: "4px 0 0", fontSize: 13 }}>{fill(t.pipeline.licencesN, { n: p.expectedLicences })}</p>
                    )}
                    {p.nextAction && (
                      <p style={{ margin: "4px 0 0", fontSize: 13, color: late ? "var(--peach-deep)" : "var(--ink-soft)", fontWeight: late ? 700 : 400 }}>
                        {p.nextActionOn
                          ? fill(t.pipeline.nextOn, { action: p.nextAction, date: formatDate(locale, p.nextActionOn) })
                          : fill(t.pipeline.next, { action: p.nextAction })}
                        {late ? ` (${t.pipeline.overdue})` : ""}
                      </p>
                    )}
                    {stage === "lost" && p.lostReason && <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--ink-soft)" }}>{p.lostReason}</p>}
                    {p.orgId && (
                      <p style={{ margin: "4px 0 0", fontSize: 13 }}>
                        <Link to={`/sales/accounts/${encodeURIComponent(p.orgId)}`} style={{ color: "var(--brand)", fontWeight: 600 }}>
                          {t.pipeline.linked}
                        </Link>
                      </p>
                    )}
                    {(onEdit || (onCreateCode && stage === "won" && !p.orgId)) && (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                        {onEdit && (
                          <Button variant="secondary" onClick={() => onEdit(p)} aria-label={`${t.common.edit}: ${p.name}`}>
                            {t.common.edit}
                          </Button>
                        )}
                        {onCreateCode && stage === "won" && !p.orgId && (
                          <Button variant="secondary" onClick={() => onCreateCode(p)} aria-label={`${t.pipeline.createCode}: ${p.name}`}>
                            {t.pipeline.createCode}
                          </Button>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}

function ProspectForm({ prospect, onDone }: { prospect: Prospect | null; onDone: (saved: boolean) => void }) {
  const { ds, t } = useSales();
  const p = prospect;
  const [name, setName] = useState(p?.name ?? "");
  const [kind, setKind] = useState<OrgKind>(p?.kind ?? "organisation");
  const [stage, setStage] = useState<ProspectStage>(p?.stage ?? "lead");
  const [contactName, setContactName] = useState(p?.contactName ?? "");
  const [contactEmail, setContactEmail] = useState(p?.contactEmail ?? "");
  const [contactPhone, setContactPhone] = useState(p?.contactPhone ?? "");
  const [expected, setExpected] = useState(p?.expectedLicences == null ? "" : String(p.expectedLicences));
  const [nextAction, setNextAction] = useState(p?.nextAction ?? "");
  const [nextOn, setNextOn] = useState(p?.nextActionOn ?? "");
  const [lostReason, setLostReason] = useState(p?.lostReason ?? "");
  const [notes, setNotes] = useState(p?.notes ?? "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: Tone; text: string } | null>(null);
  const lostMissing = stage === "lost" && lostReason.trim() === "";

  async function save() {
    if (!name.trim()) return setMsg({ tone: "attention", text: t.pipeline.errName });
    if (lostMissing) return setMsg({ tone: "attention", text: t.common.errLostReason });
    const exp = expected.trim() === "" ? null : Number(expected);
    if ((exp !== null && (!Number.isInteger(exp) || exp < 0)) || (contactEmail.trim() && !looksLikeEmail(contactEmail))) {
      return setMsg({ tone: "attention", text: t.common.errInvalid });
    }
    const n = (v: string) => (v.trim() === "" ? null : v.trim());
    const fields: ProspectFields = {
      name: name.trim(),
      kind,
      stage,
      contact_name: n(contactName),
      contact_email: n(contactEmail),
      contact_phone: n(contactPhone),
      expected_licences: exp,
      next_action: n(nextAction),
      next_action_on: nextOn || null,
      lost_reason: n(lostReason),
      notes: n(notes),
    };
    setBusy(true);
    setMsg(null);
    try {
      await ds.saveProspect(p?.id ?? null, fields);
      onDone(true);
    } catch (err) {
      setMsg({ tone: "attention", text: salesErrorCopy(t, err) });
      setBusy(false);
    }
  }

  const id = p?.id ?? "new";
  return (
    <Card title={p ? fill(t.pipeline.editTitle, { name: p.name }) : t.pipeline.newTitle}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 190px), 1fr))", gap: 10 }}>
        <Field label={t.pipeline.name} value={name} maxLength={200} required onChange={(e) => setName(e.target.value)} />
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <label htmlFor={`pk-${id}`} style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-soft)" }}>
            {t.pipeline.kind}
          </label>
          <select id={`pk-${id}`} value={kind} onChange={(e) => setKind(e.target.value as OrgKind)} style={INPUT}>
            <option value="organisation">{t.common.kindOrg}</option>
            <option value="individual">{t.common.kindIndividual}</option>
          </select>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <label htmlFor={`ps-${id}`} style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-soft)" }}>
            {t.pipeline.stage}
          </label>
          <select id={`ps-${id}`} value={stage} onChange={(e) => setStage(e.target.value as ProspectStage)} style={INPUT}>
            {PROSPECT_STAGES.map((s) => (
              <option key={s} value={s}>
                {t.pipeline[`stage_${s}`]}
              </option>
            ))}
          </select>
        </div>
        <Field label={t.pipeline.contactName} value={contactName} maxLength={120} onChange={(e) => setContactName(e.target.value)} />
        <Field label={t.pipeline.contactEmail} type="email" value={contactEmail} maxLength={254} onChange={(e) => setContactEmail(e.target.value)} />
        <Field label={t.pipeline.contactPhone} type="tel" value={contactPhone} maxLength={40} onChange={(e) => setContactPhone(e.target.value)} />
        <Field label={t.pipeline.expected} type="number" min={0} value={expected} onChange={(e) => setExpected(e.target.value)} />
        <Field label={t.pipeline.nextAction} value={nextAction} maxLength={300} onChange={(e) => setNextAction(e.target.value)} />
        <Field label={t.pipeline.nextActionOn} type="date" value={nextOn} onChange={(e) => setNextOn(e.target.value)} />
        {stage === "lost" && (
          <Field
            label={t.pipeline.lostReason}
            value={lostReason}
            maxLength={300}
            required
            error={lostMissing ? t.common.errLostReason : undefined}
            onChange={(e) => setLostReason(e.target.value)}
            style={{ gridColumn: "1 / -1" }}
          />
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 5, gridColumn: "1 / -1" }}>
          <label htmlFor={`pn-${id}`} style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-soft)" }}>
            {t.pipeline.notes}
          </label>
          <textarea id={`pn-${id}`} value={notes} maxLength={4000} rows={3} onChange={(e) => setNotes(e.target.value)} style={{ ...INPUT, resize: "vertical" }} />
        </div>
      </div>
      <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <Button onClick={() => void save()} disabled={busy}>
          {busy ? t.common.saving : t.common.save}
        </Button>
        <Button variant="secondary" onClick={() => onDone(false)} disabled={busy}>
          {t.common.cancel}
        </Button>
        <StatusLine msg={msg} />
      </div>
    </Card>
  );
}
